import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormArray,
  FormBuilder,
  FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';
import { ReportService } from '../../../services/precast-report.service';

@Component({
    selector: 'app-edit-precast-report',
    templateUrl: './edit-precast-report.component.html',
    styleUrls: ['./edit-precast-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditPrecastReportComponent implements OnInit {
  form!: FormGroup;
  today = new Date();

  previewData: any = null;
  @Input() projectId = '';
  @Input() reportId = '';
  @Input() projectReference = '';
  @Input() preApproval = false;

  previewDisabled = false;
  previewPdfDisabled = false;
  showPoints = true;
  metricUnitValue = false;
  originalThermalData: any[] = [];

  private static readonly DEFAULT_SIGNATURE_ID = 'C063AD0D-903F-447B-BCCC-A3C014CFAD7E';

  constructor(private fb: FormBuilder, private reportService: ReportService, private router: Router) {}

  ngOnInit(): void {
    this.initForm();

    this.form.get('isMetricUnit')!.valueChanges.subscribe((val: string) => {
      const newIsMetric = val === 'Metric';
      if (newIsMetric !== this.metricUnitValue) {
        this.convertThermalDataValues(this.metricUnitValue, newIsMetric);
        this.metricUnitValue = newIsMetric;
      }
    });

    if (this.projectId) {
      this.reportService.fetchReportData(this.projectId).subscribe({
        next: (data) => {
          this.previewData = data;
          this.patchForm(data);
        },
      });
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      reportDate: [null],
      isBilling: [false],
      mainBody: [''],
      thermalData: this.fb.array([]),
      commentPart1: [''],
      commentPart2: [''],
      commentPart3: [''],
      signatureTypeId: [EditPrecastReportComponent.DEFAULT_SIGNATURE_ID],
      isMetricUnit: ['Imperial'],
      companyName: [''],
      clientCompanyName: [''],
    });
  }

  private patchForm(data: any): void {
    const isMetric = !!data.isMetricUnit;
    this.metricUnitValue = isMetric;
    this.showPoints = !!data.showPoints;
    this.originalThermalData = (data.thermalData ?? []).map((r: any) => ({
      ...r,
    }));

    this.form.patchValue({
      reportDate: data.reportDate ? new Date(DateTimeUtils.getLocalDateTimeFromUTCDate(data.reportDate)) : null,
      isBilling: data.isBilling,
      mainBody: this.coerceString(data.mainBody),
      commentPart1: this.coerceString(data.commentPart1),
      commentPart2: this.coerceString(data.commentPart2),
      commentPart3: this.coerceString(data.commentPart3),
      signatureTypeId: data.signatureTypeId ?? EditPrecastReportComponent.DEFAULT_SIGNATURE_ID,
      isMetricUnit: isMetric ? 'Metric' : 'Imperial',
      companyName: this.coerceString(data.companyName),
      clientCompanyName: data.clientCompanyName,
    }, {
      emitEvent: false,
    });
    this.populateThermalArray(data.thermalData, isMetric);
  }

  private populateThermalArray(rows: any[] | null | undefined, isMetric: boolean): void {
    const thermalArray = this.form.get('thermalData') as FormArray;
    thermalArray.clear();
    (rows ?? []).forEach((r: any) => thermalArray.push(this.buildThermalRow(r, isMetric)));
  }

  private buildThermalRow(r: any, isMetric: boolean): FormGroup {
    return this.fb.group({
      sampleId: [r.sampleId],
      castDate: [r.castDate],
      materialType: [r.materialType],
      mixID: [r.mixID],
      mixReference: [r.mixReference],
      moistureContent: [r.moistureContent],
      dryDensity: [isMetric ? this.convertDensity(r.dryDensity, true) : r.dryDensity],
      wet: [this.displayTr(r.wet, isMetric)],
      dry: [this.displayTr(r.dry, isMetric)],
      precastSampleID: [r.preCastSampleID],
    });
  }

  private displayTr(value: any, isMetric: boolean): any {
    if (value === null || value === undefined || value === '') return value;
    if (isMetric) {
      const converted = this.convertThermalResistivity(value, true);
      const num = Number(converted);
      if (Number.isNaN(num)) return converted;
      return Math.round((num + Number.EPSILON) * 100) / 100;
    }
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    if (num <= 10) return Math.round((num + Number.EPSILON) * 10) / 10;
    return Math.round(num);
  }

  private coerceString(value: any): string {
    return value == null || value === '' ? '' : value;
  }

  formatCastDate(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  }

  get thermalDataArray(): FormArray {
    return this.form.get('thermalData') as FormArray;
  }

  mixDesignLink(mixId: any, mixReference: any): string {
    const params = new URLSearchParams({
      mixDesignCode: String(mixId ?? ''),
      mixReference: String(mixReference ?? ''),
    });
    return `/mix-design/search?${params.toString()}`;
  }

  compareSignatureIds = (a: string | null | undefined, b: string | null | undefined): boolean => {
    if (a == null || b == null) return a === b;
    return String(a).toLowerCase() === String(b).toLowerCase();
  };

  preview(): void {
    const formValue = this.form.value;
    const { thermalData } = formValue;
    const payload = this.buildPayload(formValue, thermalData);
    this.previewDisabled = true;
    this.reportService.createPrecastReportFromData(payload).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(blob, `${this.buildReportName(payload)}.docx`);
        this.previewDisabled = false;
      },
      error: () => {
        this.previewDisabled = false;
      },
    });
  }

  previewPdf(): void {
    const formValue = this.form.value;
    const { thermalData } = formValue;

    const payload = this.buildPayload(formValue, thermalData);
    this.previewPdfDisabled = true;
    this.reportService.createPrecastReportPDFFromData(payload).subscribe({
      next: (blob: Blob) => {
        this.openBlobInNewTab(blob, `${this.buildReportName(payload)}.pdf`);
        this.previewPdfDisabled = false;
      },
      error: () => {
        this.previewPdfDisabled = false;
      },
    });
  }

  save(): void {
    const formValue = this.form.value;
    const formThermalData = formValue.thermalData ?? [];
    const thermalData = this.originalThermalData.map((row: any, i: number) => {
      const formRow = formThermalData[i];
      if (!formRow) {
        return {
          ...row,
        };
      }
      return {
        ...row,
        sampleId: formRow.sampleId,
        mixID: formRow.mixID,
      };
    });

    this.reportService.updatePrecastReport(this.buildPayload(formValue, thermalData)).subscribe({
      next: () => {
        this.form.markAsPristine();
      },
    });
  }

  private buildPayload(formValue: any, thermalData: any[]): any {
    const reportDate = new Date(formValue.reportDate);
    const now = new Date();
    reportDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    return {
      ...this.previewData,
      reportDate,
      reportDateString: this.formatDateOnly(reportDate),
      isBilling: formValue.isBilling,
      mainBody: formValue.mainBody,
      commentPart1: formValue.commentPart1,
      commentPart2: formValue.commentPart2,
      commentPart3: formValue.commentPart3,
      signatureTypeId: formValue.signatureTypeId,
      isMetricUnit: formValue.isMetricUnit === 'Metric',
      showPoints: this.showPoints,
      companyName: formValue.companyName,
      thermalData,
      clientCompanyName: formValue.clientCompanyName,
    };
  }

  private formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private downloadBlob(blob: Blob, filename?: string): void {
    const url = window.URL.createObjectURL(blob);
    const hyperlink = document.createElement('a');
    hyperlink.href = url;
    if (filename) {
      hyperlink.download = filename;
    }
    hyperlink.click();
    window.URL.revokeObjectURL(url);
  }

  private openBlobInNewTab(blob: Blob, filename: string): void {
    const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], {
      type: 'application/pdf',
    });
    const pdfUrl = window.URL.createObjectURL(pdfBlob);
    // A blob opened directly in a tab downloads as its GUID object-URL id, so the
    // PDF is wrapped in a same-origin page whose title and Download link both use
    // the report name, matching the Word download's filename.
    const pageUrl = window.URL.createObjectURL(
      new Blob([this.buildPdfViewerPage(pdfUrl, filename)], {
        type: 'text/html',
      }),
    );
    const viewer = window.open(pageUrl, '_blank');
    if (!viewer) {
      // Popup blocked: fall back to a direct named download.
      this.downloadBlob(pdfBlob, filename);
      window.URL.revokeObjectURL(pdfUrl);
      window.URL.revokeObjectURL(pageUrl);
      return;
    }
    viewer.addEventListener('beforeunload', () => {
      window.URL.revokeObjectURL(pdfUrl);
      window.URL.revokeObjectURL(pageUrl);
    });
  }

  private buildPdfViewerPage(pdfUrl: string, filename: string): string {
    const safeName = this.escapeHtml(filename);
    return `<!doctype html><html><head><meta charset="utf-8"><title>${safeName}</title>`
      + '<style>html,body{margin:0;height:100%;font-family:Arial,sans-serif}'
      + '#bar{height:40px;display:flex;align-items:center;justify-content:flex-end;'
      + 'background:#f1f1f1;padding:0 12px;box-sizing:border-box}'
      + '#bar a{background:#3f51b5;color:#fff;text-decoration:none;padding:6px 14px;'
      + 'border-radius:4px;font-size:14px}'
      + 'iframe{border:0;width:100%;height:calc(100% - 40px);display:block}</style></head>'
      + `<body><div id="bar"><a download="${safeName}" href="${pdfUrl}">Download</a></div>`
      + `<iframe src="${pdfUrl}" type="application/pdf"></iframe></body></html>`;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildReportName(payload: any): string {
    const parts = [
      this.sanitizeNamePart(payload?.clientCompanyName),
      this.sanitizeNamePart(payload?.projectName),
      this.sanitizeNamePart(payload?.projectNumber),
      this.buildMixIdNamePart(payload?.thermalData),
      this.buildSampleTypeCode(payload?.thermalData),
      this.sanitizeNamePart(this.projectReference),
      this.formatReportDateForName(payload?.reportDate),
      this.preApproval ? 'PA' : '',
    ];
    return parts.filter((p) => p && p.length > 0).join('-');
  }

  private buildMixIdNamePart(thermalData: any[] | null | undefined): string {
    if (!this.preApproval) return '';
    const row = (thermalData ?? []).find((r: any) => this.sanitizeNamePart(r?.mixID).length > 0);
    return this.sanitizeNamePart(row?.mixID);
  }

  private buildSampleTypeCode(thermalData: any[] | null | undefined): string {
    const codes = new Set<string>();
    (thermalData ?? []).forEach((row: any) => {
      const code = this.sampleTypeCode(row?.materialType);
      if (code) codes.add(code);
    });
    return ['C', 'F', 'G', 'O'].filter((code) => codes.has(code)).join('');
  }

  private sampleTypeCode(materialType: any): string {
    const name = String(materialType ?? '').toLowerCase().trim();
    if (!name) return '';
    if (name.includes('concrete')) return 'C';
    if (name.includes('ftb')) return 'F';
    if (name.includes('grout') || name.includes('slurry')) return 'G';
    return 'O';
  }

  private sanitizeNamePart(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[\\/:*?"<>|]/g, '').trim();
  }

  private formatReportDateForName(value: any): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear() % 100).padStart(2, '0');
    return `${mm}${dd}${yy}`;
  }

  editFigures(): void {
    this.router.navigate(['/report/thermal', 'precast', this.projectId]);
  }

  formatDate(iso: string): string {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private convertThermalDataValues(fromMetric: boolean, toMetric: boolean): void {
    if (fromMetric === toMetric) return;

    this.thermalDataArray.controls.forEach((ctrl) => {
      const group = ctrl as FormGroup;

      const wetCtrl = group.get('wet');
      if (wetCtrl) {
        wetCtrl.setValue(this.convertThermalResistivity(wetCtrl.value, toMetric), {
          emitEvent: false,
        });
      }

      const dryCtrl = group.get('dry');
      if (dryCtrl) {
        dryCtrl.setValue(this.convertThermalResistivity(dryCtrl.value, toMetric), {
          emitEvent: false,
        });
      }

      const densityCtrl = group.get('dryDensity');
      if (densityCtrl) {
        densityCtrl.setValue(this.convertDensity(densityCtrl.value, toMetric), {
          emitEvent: false,
        });
      }
    });
  }

  private convertThermalResistivity(value: any, toMetric: boolean): any {
    if (value === null || value === undefined || value === '') return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    const converted = toMetric ? num / 100 : num * 100;
    return converted;
  }

  private convertDensity(value: any, toMetric: boolean): any {
    if (value === null || value === undefined || value === '') return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    const converted = toMetric ? num * 16.0185 : num / 16.0185;
    return converted;
  }
}
